import React, { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDownloadAttachment } from '../../../App.hooks';
import SpinnerNew from '../../../components/main/SpinnerNew/SpinnerNew';
import { useGetModelQuery, useGetProjectQuery } from '../../../generated/graphql';
import { groupBy } from '../../../Utils';
import EssentialsNew from '../../../components/main/EssentialsNew/EssentialsNew';
import DonutChart from '../../../components/main/DonutChart/DonutChart';
import HBarChart from '../../../components/main/HBarChart/HBarChart';
import { CSVLink } from "react-csv";
import { Download, Search, Activity, Box, Layers, CheckCircle2, XCircle, AlertTriangle, LayoutDashboard, Eye } from 'lucide-react';
import HealthCheckDetailDialog from '../../../components/dialogs/HealthCheckDetailDialog/HealthCheckDetailDialog';
import DataTableHeaderCell from '../../../components/main/DataTableNew/DataTableHeaderCell';

type HealthData = {
    Name: string;
    Group: string;
    Limit: number;
    Value: number;
    AdditionalData: string;
};

type ModelProps = {
    style?: CSSProperties | undefined;
    children?: ReactNode | undefined;
    className?: string;
};

/* ------------------------------------------------------------------ */
/*  Stat card — small KPI tile                                        */
/* ------------------------------------------------------------------ */
const StatCard = ({ label, value, icon: Icon, color }: {
    label: string; value: string | number;
    icon: React.ElementType; color: string;
}) => (
    <div className="card p-4 flex items-center gap-3 min-w-[160px]">
        <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${color}18`, color }}
        >
            <Icon size={18} />
        </div>
        <div>
            <div className="text-xl font-bold" style={{ color: 'var(--text-primary)', lineHeight: 1.2 }}>{value}</div>
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>{label}</div>
        </div>
    </div>
);

/* ------------------------------------------------------------------ */
/*  Health result row badge                                           */
/* ------------------------------------------------------------------ */
const HealthBadge = ({ value, limit }: { value: number; limit: number }) => {
    if (limit <= 0) return <span className="badge badge-default">N/A</span>;
    const pct = Math.round((value / limit) * 100);
    if (pct <= 100) return <span className="badge badge-success"><CheckCircle2 size={11} className="mr-0.5" /> {pct}%</span>;
    if (pct < 130) return <span className="badge badge-warning"><AlertTriangle size={11} className="mr-0.5" /> {pct}%</span>;
    return <span className="badge badge-danger"><XCircle size={11} className="mr-0.5" /> {pct}%</span>;
};

/* ================================================================== */
/*  Model page                                                        */
/* ================================================================== */
const Model = (props: ModelProps) => {
    const { modelId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'overview' | 'families' | 'health-check'>('overview');
    const [data, setData] = useState<any[]>([]);
    const [healthMeta, setHealthMeta] = useState<any>();
    const [sortField, setSortField] = useState<string>('Name');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    const [searchText, setSearchText] = useState('');
    const [healthSortField, setHealthSortField] = useState<string>('Name');
    const [healthSortDir, setHealthSortDir] = useState<'asc' | 'desc'>('asc');
    const [detailDialog, setDetailDialog] = useState<{ title: string; additionalData: string } | null>(null);
    const download = useDownloadAttachment();

    const model = useGetModelQuery({ variables: { id: modelId } });
    const project = useGetProjectQuery({ variables: { id: model.data?.Model?.projectId } });

    /* -- fetch & parse caches ---------------------------------------- */
    useEffect(() => {
        if (model.data && model.data.Model?.caches) {
            setIsLoading(true);
            download(model.data.Model?.caches).then(res => {
                const str = atob(res);
                const rawData = JSON.parse(str);
                const parsedData = (rawData?.Families as any[] || rawData?.Contents as any[]).flatMap(f =>
                    (f.Types as any[]).length > 0
                        ? (f.Types as any[]).map(t => ({
                            Name: t.Name, Family: f.Name, Category: f.Category,
                            Settings: t.Settings, TypeMark: t.TypeMark,
                            IfcExportAs: f.IfcExportAs, Uniclass: f.Uniclass, VBIS: f.VBIS,
                        }))
                        : {
                            Name: f.Name, Family: f.Name, Category: f.Category,
                            Settings: f.Settings, TypeMark: f.TypeMark,
                            IfcExportAs: f.IfcExportAs, Uniclass: f.Uniclass, VBIS: f.VBIS,
                        }
                );
                setHealthMeta(rawData.HeathData);
                setData(parsedData);
                setIsLoading(false);
            }).catch(() => setIsLoading(false));
        }
    }, [model]);

    /* -- derived data ------------------------------------------------ */
    const familyColumns = ['Family', 'Name', 'Category', 'TypeMark', 'IfcExportAs', 'Uniclass', 'VBIS'] as const;
    const friendlyColName = (col: string) =>
        col === 'TypeMark' ? 'Type Mark' : col === 'IfcExportAs' ? 'IFC Export As' : col;

    const filteredData = useMemo(() =>
        data.filter(row =>
            !searchText || familyColumns.some(col =>
                (row[col] ?? '').toString().toLowerCase().includes(searchText.toLowerCase()))
        ), [data, searchText]);

    const sortedData = useMemo(() =>
        [...filteredData].sort((a, b) => {
            const aVal = (a[sortField] ?? '').toString().toLowerCase();
            const bVal = (b[sortField] ?? '').toString().toLowerCase();
            return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }), [filteredData, sortField, sortDir]);

    const handleSort = (field: string) => {
        if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortField(field); setSortDir('asc'); }
    };

    const healthResults: HealthData[] = useMemo(() =>
        model.data?.Model?.healthResults
            ? (JSON.parse(model.data.Model.healthResults) as any[]).map(o => ({
                Name: o.Name, Group: o.Group, Value: o.Value, Limit: o.Limit,
                AdditionalData: (o.AdditionalData as string)?.replace(/['"]+/g, '') ?? '',
            }))
            : [],
        [model.data?.Model?.healthResults]);

    const sortedHealthResults = useMemo(() =>
        [...healthResults].sort((a, b) => {
            const aVal = (a as any)[healthSortField]?.toString().toLowerCase() ?? '';
            const bVal = (b as any)[healthSortField]?.toString().toLowerCase() ?? '';
            if (!isNaN(Number(aVal)) && !isNaN(Number(bVal)))
                return healthSortDir === 'asc' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
            return healthSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }), [healthResults, healthSortField, healthSortDir]);

    const handleHealthSort = (field: string) => {
        if (healthSortField === field) setHealthSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setHealthSortField(field); setHealthSortDir('asc'); }
    };

    /* -- category chart data ----------------------------------------- */
    const categoryChartData = useMemo(() => {
        const uniqueFamilies = groupBy(data, f => f.Family).map(g => g[0]);
        const familyByCat = groupBy(uniqueFamilies, f => f.Category)
            .map(g => ({ category: g[0].Category || '(none)', count: g.length }))
            .sort((a, b) => a.category.localeCompare(b.category));
        const typeByCat = groupBy(data, d => d.Category)
            .map(g => ({ category: g[0].Category || '(none)', count: g.length }))
            .sort((a, b) => a.category.localeCompare(b.category));
        return { familyByCat, typeByCat };
    }, [data]);

    /* -- health results summary -------------------------------------- */
    const healthSummary = useMemo(() => {
        const passing = healthResults.filter(r => r.Limit > 0 && (r.Value / r.Limit) <= 0.5).length;
        const warning = healthResults.filter(r => r.Limit > 0 && (r.Value / r.Limit) > 0.5 && (r.Value / r.Limit) <= 0.8).length;
        const failing = healthResults.filter(r => r.Limit > 0 && (r.Value / r.Limit) > 0.8).length;
        return { passing, warning, failing, total: healthResults.length };
    }, [healthResults]);

    /* -- unique family count ----------------------------------------- */
    const uniqueFamilyCount = useMemo(
        () => new Set(data.map(d => d.Family)).size,
        [data]);

    const tabs = [
        { id: 'overview' as const, label: 'Overview', icon: LayoutDashboard },
        { id: 'families' as const, label: `Families (${data.length})`, icon: Layers },
        { id: 'health-check' as const, label: `Health Checks (${healthResults.length})`, icon: Activity },
    ];

    /* ================================================================ */
    return (
        <div className="flex flex-col h-full" style={{ backgroundColor: 'var(--bg-app)' }}>
            {/* Essentials */}
            <EssentialsNew
                persistenceKey="model-details"
                className="mx-4 mt-3"
                isAdmin={project.data?.Project?.isAdmin || false}
                rows={[
                    { title: "Name", value: model?.data?.Model?.name || "" },
                    { title: "Description", value: model?.data?.Model?.description || "" },
                ]}
            />

            {/* Tab bar */}
            <div className="px-4 pt-3 pb-1">
                <div className="tab-bar" style={{ alignSelf: 'flex-start', display: 'inline-flex' }}>
                    {tabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={activeTab === tab.id ? 'tab-item-active' : 'tab-item'}
                            >
                                <Icon size={14} strokeWidth={1.5} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-auto">

                {/* ====== OVERVIEW TAB ====== */}
                {activeTab === 'overview' && (
                    <div className="p-4 flex flex-col gap-4">
                        {isLoading ? <SpinnerNew /> : (
                            <>
                                {/* KPI stat cards */}
                                <div className="flex flex-wrap gap-3">
                                    <StatCard label="Families" value={uniqueFamilyCount} icon={Box} color="var(--accent)" />
                                    <StatCard label="Family Types" value={data.length} icon={Layers} color="var(--info)" />
                                    {healthSummary.total > 0 && (
                                        <>
                                            <StatCard label="Checks Passing" value={healthSummary.passing} icon={CheckCircle2} color="var(--success)" />
                                            <StatCard label="Warnings" value={healthSummary.warning} icon={AlertTriangle} color="var(--warning)" />
                                            <StatCard label="Failing" value={healthSummary.failing} icon={XCircle} color="var(--danger)" />
                                        </>
                                    )}
                                </div>

                                {/* Charts row */}
                                <div className="grid gap-4" style={{ gridTemplateColumns: healthMeta ? '340px 1fr' : '1fr' }}>
                                    {/* Donut charts card */}
                                    {healthMeta && (
                                        <div className="card p-5">
                                            <div className="flex items-center gap-2 mb-4">
                                                <Activity size={16} style={{ color: 'var(--accent)' }} />
                                                <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                    Model Health
                                                </span>
                                            </div>
                                            <div className="flex flex-wrap justify-around gap-6">
                                                <DonutChart
                                                    title="Families"
                                                    segments={[
                                                        { label: 'Found In Library', value: healthMeta.FoundFamilyCount, color: '#22c55e' },
                                                        { label: 'Not Found', value: healthMeta.NotFoundFamilyCount, color: '#f87171' },
                                                    ]}
                                                    size={140}
                                                />
                                                <DonutChart
                                                    title="Family Types"
                                                    segments={[
                                                        { label: 'Found In Library', value: healthMeta.FoundFamilyTypeCount, color: '#22c55e' },
                                                        { label: 'Not Found', value: healthMeta.NotFoundFamilyTypeCount, color: '#f87171' },
                                                        { label: 'Not Checked', value: healthMeta.NotCheckedFamilyTypeCount, color: '#facc15' },
                                                    ]}
                                                    size={140}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    {/* Bar chart card */}
                                    {categoryChartData.familyByCat.length > 0 && (
                                        <div className="card p-5">
                                            <HBarChart
                                                title="Families by Category"
                                                series={[
                                                    { label: 'Families', color: '#6366f1', data: categoryChartData.familyByCat },
                                                    { label: 'Family Types', color: '#a78bfa', data: categoryChartData.typeByCat },
                                                ]}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Quick health results summary (top failures) */}
                                {healthResults.length > 0 && (
                                    <div className="card p-5">
                                        <div className="flex items-center justify-between mb-3">
                                            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                                Health Check Summary
                                            </span>
                                            <button className="btn btn-ghost text-xs" onClick={() => setActiveTab('health-check')}>
                                                View all →
                                            </button>
                                        </div>
                                        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                                            {healthResults.slice(0, 12).map((r, i) => {
                                                const pct = r.Limit > 0 ? Math.round((r.Value / r.Limit) * 100) : 0;
                                                const barColor = pct <= 100 ? 'var(--success)' : pct < 130 ? 'var(--warning)' : 'var(--danger)';
                                                return (
                                                    <div key={i} className="flex flex-col gap-1 p-2.5 rounded-lg" style={{ backgroundColor: 'var(--surface-50)' }}>
                                                        <div className="flex items-center justify-between">
                                                            <span className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)', maxWidth: '70%' }}>{r.Name}</span>
                                                            <span className="text-xs font-semibold" style={{ color: barColor }}>{r.Value}/{r.Limit}</span>
                                                        </div>
                                                        <div className="w-full rounded-full overflow-hidden" style={{ height: 4, backgroundColor: 'var(--surface-200)' }}>
                                                            <div
                                                                className="h-full rounded-full transition-all"
                                                                style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: barColor }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* ====== FAMILIES TAB ====== */}
                {activeTab === 'families' && (
                    <div className="flex flex-col h-full">
                        {isLoading ? <SpinnerNew /> : (
                            <>
                                {/* Toolbar */}
                                <div className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                                    <div className="relative flex-1 max-w-sm">
                                        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }} />
                                        <input
                                            type="text"
                                            placeholder="Search families…"
                                            className="input-base pl-9"
                                            value={searchText}
                                            onChange={e => setSearchText(e.target.value)}
                                        />
                                    </div>
                                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                        {sortedData.length === data.length
                                            ? `${data.length} items`
                                            : `${sortedData.length} of ${data.length} items`}
                                    </span>
                                </div>

                                {/* Table */}
                                <div className="flex-1 overflow-auto">
                                    <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                                        <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--surface-50)' }}>
                                            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                                {familyColumns.map(col => (
                                                    <DataTableHeaderCell
                                                        key={col}
                                                        id={col}
                                                        label={friendlyColName(col)}
                                                        sortField={col}
                                                        sort={{ field: sortField, direction: sortDir }}
                                                        onSort={handleSort}
                                                    />
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedData.map((row, i) => (
                                                <tr
                                                    key={i}
                                                    className="transition-colors"
                                                    style={{
                                                        borderBottom: '1px solid var(--border-subtle)',
                                                        cursor: 'default',
                                                    }}
                                                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bg-hover)')}
                                                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                                                >
                                                    {familyColumns.map(col => (
                                                        <td key={col} className="px-3 py-2" style={{ color: 'var(--text-primary)' }}>
                                                            {row[col] || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {sortedData.length === 0 && (
                                        <div className="empty-state">
                                            <div className="empty-state-icon"><Search size={24} /></div>
                                            <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No families match your search</span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* ====== HEALTH CHECK RESULTS TAB ====== */}
                {activeTab === 'health-check' && (
                    <div className="flex flex-col h-full">
                        {/* Toolbar */}
                        <div className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                            <CSVLink
                                style={{ textDecoration: 'none' }}
                                filename={`${model?.data?.Model?.name || 'health-results'}.csv`}
                                data={healthResults}
                            >
                                <button className="btn btn-secondary text-xs">
                                    <Download size={14} /> Export CSV
                                </button>
                            </CSVLink>
                            <div className="flex-1" />
                            {healthSummary.total > 0 && (
                                <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--success)' }} /> {healthSummary.passing} passing</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--warning)' }} /> {healthSummary.warning} warning</span>
                                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--danger)' }} /> {healthSummary.failing} failing</span>
                                </div>
                            )}
                        </div>

                        {isLoading ? <SpinnerNew /> : (
                            <div className="flex-1 overflow-auto">
                                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                                    <thead className="sticky top-0 z-10" style={{ backgroundColor: 'var(--surface-50)' }}>
                                        <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                            {(['Name', 'Group', 'Limit', 'AdditionalData', 'Value'] as const).map(col => (
                                                <DataTableHeaderCell
                                                    key={col}
                                                    id={col}
                                                    label={col === 'AdditionalData' ? 'Details' : col}
                                                    sortField={col}
                                                    sort={{ field: healthSortField, direction: healthSortDir }}
                                                    onSort={handleHealthSort}
                                                />
                                            ))}
                                            <th scope="col" className="px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                                                Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedHealthResults.map((row, i) => (
                                            <tr
                                                key={i}
                                                className="transition-colors"
                                                style={{ borderBottom: '1px solid var(--border-subtle)' }}
                                                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bg-hover)')}
                                                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                                            >
                                                <td className="px-3 py-2 font-medium" style={{ color: 'var(--text-primary)' }}>{row.Name}</td>
                                                <td className="px-3 py-2">
                                                    <span className="badge badge-default">{row.Group}</span>
                                                </td>
                                                <td className="px-3 py-2" style={{ color: 'var(--text-secondary)' }}>{row.Limit}</td>
                                                <td className="px-3 py-2 max-w-[200px]" style={{ color: 'var(--text-secondary)' }}>
                                                    {row.AdditionalData ? (
                                                        <button
                                                            className="inline-flex items-center gap-1.5 text-xs rounded-md px-2 py-1 transition-colors"
                                                            style={{
                                                                background: 'var(--surface-100, var(--surface-50))',
                                                                border: '1px solid var(--border-default)',
                                                                color: 'var(--accent)',
                                                                cursor: 'pointer',
                                                                fontFamily: 'inherit',
                                                            }}
                                                            title="View details"
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setDetailDialog({ title: row.Name, additionalData: row.AdditionalData });
                                                            }}
                                                        >
                                                            <Eye size={13} /> View
                                                        </button>
                                                    ) : (
                                                        <span style={{ color: 'var(--text-tertiary)' }}>—</span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-2 font-medium" style={{ color: 'var(--text-primary)' }}>{row.Value}</td>
                                                <td className="px-3 py-2">
                                                    <HealthBadge value={row.Value} limit={row.Limit} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {healthResults.length === 0 && (
                                    <div className="empty-state">
                                        <div className="empty-state-icon"><Activity size={24} /></div>
                                        <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>No health check results available</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Health Check Detail Dialog */}
            <HealthCheckDetailDialog
                visible={detailDialog !== null}
                title={detailDialog?.title ?? ''}
                additionalData={detailDialog?.additionalData ?? ''}
                onClose={() => setDetailDialog(null)}
            />
        </div>
    );
};

export default Model;
